import React, {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';

import {Headline} from 'src/admin-rights-service/components/headline';
import {Skeleton} from 'src/components/skeleton';
import {ROUTES} from 'src/constants';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {IconButton} from '@pb/kit-component-icon-button';
import {Tooltip} from '@pb/kit-component-popups';


import {ProfileName} from '../components/profile-name';
import {RemoveRoleController} from '../components/remove-role';

import {ProfileHeadlineSkeleton} from './profile-headline.skeleton';

import type {AccessRightsProfile} from 'src/openapi/admin/models/AccessRightsProfile';

type Props = {
    isSaveBusy: boolean,
    isProfileChanged: boolean,
    isLoading: boolean,

    profileId: number,
    profile: AccessRightsProfile,

    onProfileNameSet: (profileName: string) => void,
    onSave: () => void,
    onDiscard: () => void,
};

export const ProfileHeadline = (props: Props) => {
    const {
        isSaveBusy,
        isProfileChanged,
        isLoading,

        profileId,
        profile,

        onProfileNameSet,
        onSave,
        onDiscard,
    } = props;

    const navigate = useNavigate();
    const {roleCreate} = useEventTracking();

    const backToRoles = useCallback(() => {
        navigate('/roles/');
    }, [navigate]);

    return (
        <Skeleton shown={isLoading} view={<ProfileHeadlineSkeleton/>}>
            <Headline
                prefix={(
                    <Group alignItems="center">
                        <Button variant="Type.SECONDARY" startIcon={<Icon type={Icons.ANGLE_LEFT}/>} onClick={backToRoles}>
                            Back to Roles
                        </Button>
                        <ProfileName profileName={profile.profileName} onSetName={onProfileNameSet}/>
                    </Group>
                )}
                header=""
                actionButtons={(
                    <Group>
                        <RemoveRoleController
                            onDelete={backToRoles}
                            profileId={profileId}
                            profileName={profile.profileName}
                            isPrimary={profile.isPrimary}
                            usersCounts={profile.usersCounts}>
                            {({onShowRemoveModal, tooltip}) => {
                                return (
                                    <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                                        <IconButton
                                            ref={anchorRef}
                                            {...getAnchorProps()}
                                            disabled={!!tooltip}
                                            iconProps={{type: Icons.TRASH}}
                                            onClick={onShowRemoveModal}
                                            flat={true}
                                        />
                                    )}>
                                        {tooltip || 'Delete Role'}
                                    </Tooltip>
                                );
                            }}
                        </RemoveRoleController>
                        <Button
                            variant="Type.TERTIARY"
                            onClick={() => {
                                roleCreate({
                                    isDuplicate: true,
                                    place: 'Role Profile',
                                });
                                navigate(ROUTES.CREATE_ROLE, {state: {
                                    duplicateProfileId: profileId,
                                }});
                            }}>
                            Duplicate
                        </Button>
                        <Button variant="Type.SECONDARY" onClick={backToRoles}>
                            Cancel
                        </Button>
                        <Button variant="Type.SECONDARY" disabled={!isProfileChanged} onClick={onDiscard}>
                            Discard Changes
                        </Button>
                        <Button disabled={!isProfileChanged} busy={isSaveBusy} variant="Type.PRIMARY" onClick={onSave}>
                            Save Changes
                        </Button>
                    </Group>
                )}
            />
        </Skeleton>
    );
};
